import {
  FILE_TOO_LARGE_WORDS,
  MAX_DOCUMENT_BYTES,
  type ActForPerson,
  type ActForRole,
  type OthersRow,
  type ScanStatus,
  type WaitingOnOthers,
} from '@myrewards/contracts';
import { isApiError } from '../api/errors';
import { amountWords, problemWords } from '../ops/labels';
import {
  daysBetween,
  formatCount,
  formatMoney,
  formatShortDate,
  formatTimeOfDay,
  organizationDate,
  plural,
  type CardHelpContent,
  type StatusTone,
} from '../ui';

// APP-148: the words of a Stock Admin acting for an Executive or a Sector Lead, as the canvas
// boards draw them (ForExec, LeadApprove, LeadSend, Approved, Queue, Empty).

/** "Executive", "Sector Lead": the role in "(Executive)" on the Recorded as line. */
export const ROLE_WORD: Record<ActForRole, string> = {
  executive: 'Executive',
  sector_lead: 'Sector Lead',
};

/** The Stock Admin's own role, as Recorded as names it. */
export const ACTOR_ROLE = 'Stock Admin';

/** What the step's drop zone takes: the app's one file rule (D15), 25 MB each. */
export const FILE_RULE =
  'Screenshots, emails, PDFs, Word or Excel files · as many as you like · up to 25 MB each';

/** The app's upload limit (the contracts' one number): refused before it's sent, as the server would. */
export const MAX_FILE_BYTES = MAX_DOCUMENT_BYTES;

/** Why, in the server's own words (M4: one source with its 413). */
export const TOO_BIG = FILE_TOO_LARGE_WORDS;

/** The most files one action carries (the `ActingFor.fileIds` cap). The board's words stay. */
export const MAX_FILES = 20;

export const TOO_MANY_FILES =
  'That’s 20 files, the most one action can carry. Remove one to add another.';

/** The virus check's verdicts that keep a file off the action (the scan gate). */
export function isBlocked(status: ScanStatus): boolean {
  return status === 'malicious' || status === 'unscannable';
}

/** Under a blocked file in the step: what to do about it. */
export const BLOCKED_WORDS: Record<'malicious' | 'unscannable', string> = {
  malicious: 'It can’t go with this action. Remove it to go on.',
  unscannable: 'It can’t go with this action. Remove it, then add it again.',
};

/** A staged file whose check can no longer be read (the server has no such file now, R57). */
export const UNREADABLE_WORDS = 'We couldn’t check this file. Remove it and add it again.';

/**
 * How long before a pending virus check is read again (R47, R57): every 4 s while reads answer;
 * after a read fails, 8 s, then 16 s, then every 30 s until one answers.
 */
export function scanPollDelay(failures: number): number {
  return [4_000, 8_000, 16_000][failures] ?? 30_000;
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** "EB" for Elena Brightwater. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * Under a step that sends an award back or ends it, or changes a budget: nothing is held, and the
 * person can say so (D17). "If it isn't what Elena wanted, Elena can say so from the notice."
 */
export function saySoFromTheNotice(person: ActForPerson): string {
  const name = firstName(person.name);
  return `If it isn’t what ${name} wanted, ${name} can say so from the notice.`;
}

/** "Approved by Meredith Okonkwo (Stock Admin) for Elena Brightwater (Executive)". */
export function recordedLine(
  verb: string,
  actorName: string,
  person: ActForPerson,
  role: ActForRole,
): string {
  return `${verb} by ${actorName} (${ACTOR_ROLE}) for ${person.name} (${ROLE_WORD[role]})`;
}

/** "Meredith Okonkwo (Stock Admin) for Elena Brightwater (Executive)": the Done by row. */
export function doneByLine(actorName: string, person: ActForPerson, role: ActForRole): string {
  return `${actorName} (${ACTOR_ROLE}) for ${person.name} (${ROLE_WORD[role]})`;
}

/**
 * What the notice carries, after "Elena gets a notice in the bell and by email": nothing more,
 * ", with your note", ", with your note and the file", ", with the 2 files you added".
 */
export function withNoteAndFiles(note: boolean, files: number): string {
  const these = files === 1 ? 'the file' : `the ${String(files)} files`;
  if (note && files > 0) return `, with your note and ${these}`;
  if (note) return ', with your note';
  if (files > 0) return `, with ${these} you added`;
  return '';
}

/**
 * The time of day without its zone ("9:12 AM"), in the organization's time, for a pasted file's
 * name. The colon becomes a dot: the app's file-name rule replaces a colon (Windows forbids it), so
 * the name the person sees is the name that's kept.
 */
function clockForName(at: Date): string {
  return formatTimeOfDay(at.toISOString())
    .replace(/\s+\S+$/, '')
    .replace(':', '.');
}

const PASTED_EXTENSION: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
};

/** "Pasted screenshot, Oct 23 9.12 AM.png" (D16): when it was pasted, in the organization's time. */
export function pastedName(at: Date, type: string): string {
  const extension = PASTED_EXTENSION[type] ?? (type.split('/')[1] || 'png');
  return `Pasted screenshot, ${formatShortDate(at.toISOString())} ${clockForName(at)}.${extension}`;
}

/** Where a file in the step came from. */
export type FileSource = 'pasted' | 'dropped' | 'chosen';

export const SOURCE_WORDS: Record<FileSource, string> = {
  pasted: 'pasted just now',
  dropped: 'dragged in',
  chosen: 'chosen from your files',
};

/** "214 KB". */
export function sizeWords(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} bytes`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The virus check in the step's words: "Checking" or "Checked" (and what can go wrong). */
export const SCAN_WORDS: Record<ScanStatus, { label: string; tone: StatusTone }> = {
  pending: { label: 'Checking', tone: 'waiting' },
  clean: { label: 'Checked', tone: 'done' },
  not_required: { label: 'Ready', tone: 'done' },
  malicious: { label: 'Blocked: the virus check found a problem', tone: 'problem' },
  unscannable: { label: 'Couldn’t be checked', tone: 'problem' },
};

/** A step's own check before anything is sent ("Enter the funding addition…"): its words show. */
export class StepProblem extends Error {}

/**
 * Why the server refused, in its own words: a field's message (problem+json `issues`), else the
 * refusal's detail ("Acting for Executives is turned off in Roles & lists.", "Choose a current
 * Executive."), else the app's words for the code. A step's own check says its own words.
 */
export function actForProblemWords(error: unknown): string {
  if (error instanceof StepProblem) return error.message;
  if (isApiError(error)) {
    const issue = error.problem?.issues?.find((i) => i.message.trim() !== '')?.message;
    if (issue !== undefined) return issue;
    const detail = error.problem?.detail;
    if (detail !== undefined && detail.trim() !== '' && !/^[a-z_]+$/.test(detail)) return detail;
  }
  return problemWords(error);
}

/**
 * A refusal that means the page's facts moved on (the switch went off, the person named left the
 * role, the award moved): the page reads them again, so its "for an Executive" buttons and the
 * people offered follow.
 */
export function refusalRefreshes(error: unknown): boolean {
  return isApiError(error) && error.status >= 400 && error.status < 500 && error.status !== 401;
}

/** "waiting 7 days", "waiting 1 day", "waiting since today": counted to the page's day (R38). */
export function waitingDays(since: string, asOf: string): string {
  const days = daysBetween(organizationDate(since), asOf);
  return days <= 0 ? 'waiting since today' : `waiting ${plural(days, 'day', 'days')}`;
}

/** "today at 11:24 AM", "yesterday at 4:20 PM", "on Oct 9 at 4:20 PM": for "Last done". */
export function whenWords(at: string, asOf: string): string {
  const time = formatTimeOfDay(at).replace(/\s+\S+$/, '');
  const days = daysBetween(organizationDate(at), asOf);
  if (days === 0) return `today at ${time}`;
  if (days === 1) return `yesterday at ${time}`;
  return `on ${formatShortDate(at)} at ${time}`;
}

// --- Waiting on others, as the Queue board draws a row (R46) ------------------------------------

/** "Waiting on the Executives", "Waiting on Graham Whitlock", "Returned to Victor Osei". */
export function othersPill(state: OthersRow['state']): string {
  switch (state.kind) {
    case 'executives':
      return 'Waiting on the Executives';
    case 'lead':
      return `Waiting on ${state.lead.name}`;
    case 'returned':
      return `Returned to ${state.lead.name}`;
  }
}

/** "Sector Discretionary Bonus · 8 people · $42,000", "… · 1 person · $8,000 + 51 options". */
export function othersTypeLine(row: OthersRow): string {
  return `${row.typeName} · ${plural(row.people, 'person', 'people')} · ${amountWords(row.amount, row.options ?? 0)}`;
}

/**
 * The row's meta line, ending in how long it has waited, counted to the page's day (R38):
 * - over budget: "Entered by Hannah Kowalski · Range test support · $2,913 over · waiting 7 days"
 * - a PAA or TAA: "Entered by Graham Whitlock · Airborne lidar upgrade · Executive decision · …"
 * - a delegate's entry: "Entered by Sam Ortega for Graham Whitlock · Maritime Airborne · COBRA
 *   flight test · a delegate’s entry to approve · …"
 * - returned: "Space Systems · Orbital debris tracking · returned by Naomi Castellanos: “…” · …"
 * - Spot funding: "Requested by Tyler Brandt · Terrestrial · Spot funding · …"
 */
export function othersMetaLine(row: OthersRow, asOf: string): string {
  const entered =
    row.enteredBy === null
      ? null
      : `Entered by ${row.enteredBy.name}${row.enteredFor === null ? '' : ` for ${row.enteredFor.name}`}`;
  const why = row.why;
  const parts: (string | null)[] = (() => {
    switch (why.kind) {
      case 'over':
        return [
          entered,
          row.awardTitle,
          why.shortfall === null ? null : `${formatMoney(why.shortfall)} over`,
        ];
      case 'executive_decision':
        return [entered, row.awardTitle, 'Executive decision'];
      case 'delegate_entry':
        return [entered, row.sector, row.awardTitle, 'a delegate’s entry to approve'];
      case 'returned': {
        const by = why.by === null ? 'returned' : `returned by ${why.by.name}`;
        return [row.sector, row.awardTitle, why.note === null ? by : `${by}: “${why.note}”`];
      }
      case 'spot_funding':
        return [
          row.enteredBy === null ? null : `Requested by ${row.enteredBy.name}`,
          row.sector,
          'Spot funding',
        ];
    }
  })();
  return [...parts, waitingDays(row.since, asOf)]
    .filter((part): part is string => part !== null && part.trim() !== '')
    .join(' · ');
}

// --- Waiting on others: its sections, as Approvals and Home both read them --------------------

export interface OthersSection {
  id: OthersRow['section'];
  role: ActForRole;
  title: string;
  /** The section's one line when nothing waits in it. */
  empty: string;
  help: CardHelpContent;
}

export const OTHERS_SECTIONS: readonly OthersSection[] = [
  {
    id: 'over_budget',
    role: 'executive',
    title: 'Executives · over budget',
    empty: 'No over-budget awards are waiting on the Executives.',
    help: {
      shows:
        'Awards that would take a pool below zero, waiting for any one Executive to approve a top-up or decline them. Oldest first.',
      canDo:
        'When an Executive asks you to, open one and approve it with the top-up, or decline it, for them. You say who it’s for; files and a note are up to you.',
    },
  },
  {
    id: 'paa_taa',
    role: 'executive',
    title: 'Executives · PAA and TAA',
    empty: 'No PAA or TAA is waiting on the Executives.',
    help: {
      shows:
        'Professional and Team Achievement Awards waiting for any one Executive’s decision. Oldest first.',
      canDo: 'When an Executive asks you to, open one and decide it for them.',
    },
  },
  {
    id: 'spot_funding',
    role: 'executive',
    title: 'Executives · Spot funding',
    empty: 'No Spot Award funding requests are waiting on the Executives.',
    help: {
      shows: 'Spot Award funding requests waiting for an Executive’s decision. Oldest first.',
      canDo: 'When an Executive asks you to, open one and approve or decline the funding for them.',
    },
  },
  {
    id: 'executive_delegates',
    role: 'executive',
    title: 'Executives · delegates’ entries',
    empty: 'No delegate’s entry is waiting on an Executive.',
    help: {
      shows:
        'Entries from delegates who need their Executive’s OK, each waiting on the Executive it was entered for. Oldest first.',
      canDo: 'When that Executive asks you to, open one and decide it for them.',
    },
  },
  {
    id: 'sector_leads',
    role: 'sector_lead',
    title: 'Sector Leads',
    empty:
      'Nothing is waiting on a Sector Lead: no delegate’s entry to approve and no award returned to fix.',
    help: {
      shows:
        'Delegates’ entries waiting on their Sector Lead, and awards returned to a Sector Lead to fix and resend. Oldest first.',
      canDo: 'When a Sector Lead asks you to, open one and do it for them.',
    },
  },
];

/**
 * A switch that's off, in words (R142): its role's work isn't listed (D1: a switch off takes its
 * rows out), which never means it has none. Null while both are on.
 */
export function switchedOffWords(switches: WaitingOnOthers['switches']): string | null {
  const { executives, sectorLeads } = switches;
  if (executives && sectorLeads) return null;
  if (!executives && !sectorLeads)
    return 'Acting for Executives and for Sector Leads is turned off in Roles & lists, so their work isn’t listed here.';
  return executives
    ? 'Acting for Sector Leads is turned off in Roles & lists, so their work isn’t listed here.'
    : 'Acting for Executives is turned off in Roles & lists, so their decisions aren’t listed here.';
}

/**
 * Waiting on others with nothing listed (R142): Home's one line and its hero sentence, and
 * Approvals' headline and body. With a switch off they say what isn't listed, and never that
 * nothing is waiting on that role or that it's caught up.
 */
export function othersNothingWords(switches: WaitingOnOthers['switches']): {
  line: string;
  hero: string;
  headline: string;
  body: string;
} {
  const off = switchedOffWords(switches);
  if (off === null)
    return {
      line: 'Nothing is waiting on the Executives or the Sector Leads. Their decisions, delegates’ entries and returned awards show here, oldest first.',
      hero: 'Nothing is waiting on the Executives or the Sector Leads.',
      headline: 'The Executives and Sector Leads are caught up',
      body: 'An award shows here when it needs an Executive’s decision, when a delegate’s entry waits on its Sector Lead, or when an award is returned to a Sector Lead to fix.',
    };
  if (!switches.executives && !switches.sectorLeads)
    return { line: off, hero: off, headline: 'Acting for others is turned off', body: off };
  const none = switches.executives
    ? 'No decisions are waiting on the Executives'
    : 'No work is waiting on the Sector Leads';
  const shows = switches.executives
    ? 'An award shows here when it needs an Executive’s decision.'
    : 'An award shows here when a delegate’s entry waits on its Sector Lead, or when an award is returned to a Sector Lead to fix.';
  return {
    line: `${none}. ${off}`,
    hero: `${none}. ${off}`,
    headline: none,
    body: `${off} ${shows}`,
  };
}

/**
 * Whose work Home's Waiting on others lists (R142): "Work that belongs to the Executives and the
 * Sector Leads, oldest first, as they see it.", or only the role whose switch is on, saying what
 * isn't listed.
 */
export function othersIntro(switches: WaitingOnOthers['switches']): string {
  const off = switchedOffWords(switches);
  if (off === null)
    return 'Work that belongs to the Executives and the Sector Leads, oldest first, as they see it.';
  const whose = switches.executives ? 'the Executives' : 'the Sector Leads';
  return `Work that belongs to ${whose}, oldest first, as they see it. ${off}`;
}

/**
 * Whose work the switches leave on, for a hero's number (R149): "Executives and Sector Leads",
 * "Executives" or "Sector Leads". Home's and Approvals' "· waiting" both say it this way.
 */
export function othersWhose(switches: WaitingOnOthers['switches']): string {
  if (switches.executives && switches.sectorLeads) return 'Executives and Sector Leads';
  return switches.executives ? 'Executives' : 'Sector Leads';
}

/** The sections the switches leave on (D1: a switch off takes its rows out). */
export function sectionsOn(switches: WaitingOnOthers['switches']): OthersSection[] {
  return OTHERS_SECTIONS.filter((section) =>
    section.role === 'executive' ? switches.executives : switches.sectorLeads,
  );
}

/** "3 for the Executives · 2 for Sector Leads", for the switches that are on. */
export function othersMeta(others: WaitingOnOthers): string {
  return [
    others.switches.executives
      ? `${formatCount(others.counts.executives)} for the Executives`
      : null,
    others.switches.sectorLeads
      ? `${formatCount(others.counts.sectorLeads)} for ${others.counts.sectorLeads === 1 ? 'a Sector Lead' : 'Sector Leads'}`
      : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/** Where the full list is: Approvals, which a Stock Admin sees as "Waiting on others". */
export const ALL_OF_THEM_HREF = '/approvals';

/** The rows Home shows: those the switches leave on, oldest first. */
export function othersOnHome(others: WaitingOnOthers): OthersRow[] {
  const sections = sectionsOn(others.switches);
  return others.rows
    .filter((row) => sections.some((section) => section.id === row.section))
    .sort((a, b) => a.since.localeCompare(b.since) || a.key.localeCompare(b.key));
}
